import { fetchWithFullyQualifiedUrl } from "lib/fetch";

export interface DatasetMeta {
  id: string;
  label: string;
  description: string;
}

export interface ColumnMeta {
  field: string;
  // DuckDB type string e.g. "VARCHAR", "INTEGER", "DATE", "TIMESTAMP"
  type: string;
  // Optional server-side display hint, maps to a value formatter in columnDefs/shared.ts
  format?: string;
  label?: string;
  aggFunc?: string;
  hide?: boolean;
}

export interface DatasetsResponse {
  datasets: DatasetMeta[];
}

// Response from /<dataset>/meta/ — describes columns and the dataset's preferred initial sort.
// default_sort is informational; the grid does not yet apply it automatically (saved views take
// precedence when present; without one the grid falls back to server-side ordering).
export interface MetaResponse {
  default_sort: Array<{ col_id: string; sort: string }>;
  columns: ColumnMeta[];
}

// Row data uses snake_case per platform convention; AG Grid's own success callback still expects
// its native rowData / rowCount names (mapped in datasource.ts).
export interface RowsResponse {
  row_data: unknown[];
  row_count: number;
}

async function apiFetch<T>(
  path: string,
  roleSlug: string,
  options?: RequestInit,
): Promise<T> {
  const res = await fetchWithFullyQualifiedUrl(`/dataexplorer${path}`, {
    headers: {
      "Content-Type": "application/json",
      "X-User-Role": roleSlug,
    },
    ...options,
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json() as Promise<T>;
}

export const fetchDatasets = (roleSlug: string): Promise<DatasetsResponse> =>
  apiFetch<DatasetsResponse>("/datasets/", roleSlug);

export const fetchMeta = (
  datasetId: string,
  roleSlug: string,
): Promise<MetaResponse> =>
  apiFetch<MetaResponse>(`/${datasetId}/meta/`, roleSlug);

export const fetchRows = (
  datasetId: string,
  body: unknown,
  roleSlug: string,
): Promise<RowsResponse> =>
  apiFetch<RowsResponse>(`/${datasetId}/rows/`, roleSlug, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const fetchColumnValues = (
  datasetId: string,
  colId: string,
  roleSlug: string,
  scope: string,
): Promise<{ values: unknown[] }> =>
  apiFetch<{ values: unknown[] }>(
    `/${datasetId}/values/${colId}/?data_scope=${encodeURIComponent(scope)}`,
    roleSlug,
  );
